import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { NAV_ITEMS, LINKS } from "../data/portfolio";

export const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export const Navbar = () => {
  const [active, setActive] = useState("home");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    const observed = new Set();
    const tryObserve = () => {
      NAV_ITEMS.forEach(({ id }) => {
        if (observed.has(id)) return;
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
          observed.add(id);
        }
      });
      return observed.size === NAV_ITEMS.length;
    };
    tryObserve();
    const interval = setInterval(() => {
      if (tryObserve()) clearInterval(interval);
    }, 700);
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearInterval(interval);
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed left-0 right-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "glass-panel" : "border-b border-transparent"
        }`}
        data-testid="main-nav"
      >
        <nav
          aria-label="Primary"
          className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12"
        >
          <button
            onClick={() => handleNav("home")}
            data-testid="nav-logo"
            aria-label="Go to home"
            className="font-display text-lg font-extrabold tracking-tight text-[#FDFBF7]"
          >
            PS<span className="text-[#FF5E36]">.</span>
          </button>

          <div className="hidden items-center gap-8 lg:flex">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                data-testid={`nav-link-${item.id}`}
                className={`link-underline font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  active === item.id ? "text-[#00F2FE]" : "text-[#F4EFEA]/70 hover:text-[#FDFBF7]"
                }`}
                aria-current={active === item.id ? "true" : undefined}
              >
                {item.label}
              </button>
            ))}
            <a
              href={LINKS.resume}
              download="Prithwish_Sahoo_Resume.pdf"
              data-testid="nav-resume-btn"
              className="flex items-center gap-2 rounded-full border border-[#FF5E36] px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-[#FF5E36] transition-colors duration-300 hover:bg-[#FF5E36] hover:text-[#0D0D0D]"
            >
              <Download size={13} /> Resume
            </a>
          </div>

          <button
            className="text-[#FDFBF7] lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-hamburger"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#0D0D0D]/95 px-8 backdrop-blur-xl lg:hidden"
            data-testid="mobile-drawer"
          >
            {NAV_ITEMS.map((item, i) => (
              <motion.button
                key={item.id}
                initial={{ x: -40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.08 * i, duration: 0.4 }}
                onClick={() => handleNav(item.id)}
                data-testid={`mobile-nav-link-${item.id}`}
                className={`py-4 text-left font-display text-4xl font-extrabold ${
                  active === item.id ? "text-[#00F2FE]" : "text-[#FDFBF7]"
                }`}
              >
                <span className="mr-4 font-mono text-sm text-[#4A5568]">0{i + 1}</span>
                {item.label}
              </motion.button>
            ))}
            <a
              href={LINKS.resume}
              download="Prithwish_Sahoo_Resume.pdf"
              data-testid="mobile-resume-btn"
              className="mt-8 flex w-fit items-center gap-2 rounded-full border border-[#FF5E36] px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-[#FF5E36]"
            >
              <Download size={14} /> Download Resume
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
